"use client";

import { c as _c } from "react/compiler-runtime";
import { useHotkey } from "@tanstack/react-hotkeys";
import { useAtom } from "jotai";
import { useCallback, useEffect, useRef, useState } from "react";
import { chatOpenAtom } from "@/lib/atoms/chat-atoms";
import { usePreferencesHydrated, usePreferencesStore } from "@/lib/stores/preferences-store";
import { CHAT_HOTKEY, ChatFab } from "./chat-fab";
interface LoadedChatClientProps {
  suggestions?: string[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onReady: () => void;
}
type ChatClientModule = {
  ChatClient: React.ComponentType<LoadedChatClientProps>;
};
type ChatClientLoader = () => Promise<ChatClientModule>;
const loadChatClient: ChatClientLoader = () => import("./chat-client");
export function ChatClientLazy(t0) {
  const $ = _c(44);
  const {
    suggestions,
    loader: t1
  } = t0;
  const loader = t1 === undefined ? loadChatClient : t1;
  const hydrated = usePreferencesHydrated();
  const hideAiFeatures = usePreferencesStore(_temp);
  const setHideAiFeatures = usePreferencesStore(_temp2);
  const [open, setOpen] = useAtom(chatOpenAtom);
  const [ChatClient, setChatClient] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const loadingRef = useRef(false);
  const loadPromiseRef = useRef(null);
  let t2;
  if ($[0] !== loader) {
    t2 = () => {
      if (loadPromiseRef.current) {
        return loadPromiseRef.current;
      }
      const promise = loader().catch(error => {
        loadPromiseRef.current = null;
        throw error;
      });
      loadPromiseRef.current = promise;
      return promise;
    };
    $[0] = loader;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const load = t2;
  let t3;
  if ($[2] !== ChatClient || $[3] !== load) {
    t3 = () => {
      if (!ChatClient) {
        load().catch(_temp3);
      }
    };
    $[2] = ChatClient;
    $[3] = load;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const handlePrefetch = t3;
  let t4;
  let t5;
  if ($[5] !== hideAiFeatures || $[6] !== hydrated || $[7] !== setHideAiFeatures) {
    t4 = () => {
      if (!hydrated) {
        return;
      }
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get("show_ai") === "1" && hideAiFeatures) {
        setHideAiFeatures(false);
        urlParams.delete("show_ai");
        const newUrl = urlParams.toString() === "" ? window.location.pathname : `${window.location.pathname}?${urlParams.toString()}`;
        window.history.replaceState({}, "", newUrl);
      }
    };
    t5 = [hydrated, hideAiFeatures, setHideAiFeatures];
    $[5] = hideAiFeatures;
    $[6] = hydrated;
    $[7] = setHideAiFeatures;
    $[8] = t4;
    $[9] = t5;
  } else {
    t4 = $[8];
    t5 = $[9];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[10] !== hideAiFeatures || $[11] !== setOpen) {
    t6 = () => {
      if (hideAiFeatures) {
        setOpen(false);
      }
    };
    t7 = [hideAiFeatures, setOpen];
    $[10] = hideAiFeatures;
    $[11] = setOpen;
    $[12] = t6;
    $[13] = t7;
  } else {
    t6 = $[12];
    t7 = $[13];
  }
  useEffect(t6, t7);
  let t8;
  if ($[14] !== ChatClient || $[15] !== load || $[16] !== setOpen) {
    t8 = () => {
      if (ChatClient) {
        setOpen(true);
        return;
      }
      if (loadingRef.current) {
        return;
      }
      loadingRef.current = true;
      setLoading(true);
      setLoadError(false);
      setOpen(true);
      load().then(module => {
        setChatClient(() => module.ChatClient);
      }).catch(() => {
        loadingRef.current = false;
        setLoading(false);
        setLoadError(true);
        setOpen(false);
      });
    };
    $[14] = ChatClient;
    $[15] = load;
    $[16] = setOpen;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const handleOpen = t8;
  let t9;
  if ($[18] !== ChatClient || $[19] !== handleOpen || $[20] !== setOpen) {
    t9 = () => {
      if (ChatClient) {
        setOpen(_temp4);
        return;
      }
      handleOpen();
    };
    $[18] = ChatClient;
    $[19] = handleOpen;
    $[20] = setOpen;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const handleActivation = t9;
  const t10 = hydrated && !hideAiFeatures;
  let t11;
  if ($[22] !== hideAiFeatures) {
    t11 = hideAiFeatures ? undefined : {
      name: "Toggle AI chat",
      description: "Open or close the AI assistant",
      group: "Global"
    };
    $[22] = hideAiFeatures;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t10 || $[25] !== t11) {
    t12 = {
      conflictBehavior: "allow",
      enabled: t10,
      meta: t11
    };
    $[24] = t10;
    $[25] = t11;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  useHotkey(CHAT_HOTKEY, handleActivation, t12);
  let t13;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => {
      loadingRef.current = false;
      setLoading(false);
    };
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  const handleReady = t13;
  if (!hydrated) {
    return null;
  }
  let t14;
  if ($[28] !== handleActivation || $[29] !== handlePrefetch || $[30] !== hideAiFeatures || $[31] !== loading) {
    t14 = !hideAiFeatures && <ChatFab loading={loading} onClick={handleActivation} onPrefetch={handlePrefetch} />;
    $[28] = handleActivation;
    $[29] = handlePrefetch;
    $[30] = hideAiFeatures;
    $[31] = loading;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  const t15 = loadError ? "Chat failed to load. Try again." : "";
  let t16;
  if ($[33] !== t15) {
    t16 = <p className="sr-only" aria-live="polite">{t15}</p>;
    $[33] = t15;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== ChatClient || $[36] !== open || $[37] !== setOpen || $[38] !== suggestions) {
    t17 = ChatClient && <ChatClient suggestions={suggestions} open={open} onOpenChange={setOpen} onReady={handleReady} />;
    $[35] = ChatClient;
    $[36] = open;
    $[37] = setOpen;
    $[38] = suggestions;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== t14 || $[41] !== t16 || $[42] !== t17) {
    t18 = <>{t14}{t16}{t17}</>;
    $[40] = t14;
    $[41] = t16;
    $[42] = t17;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  return t18;
}
function _temp4(current) {
  return !current;
}
function _temp3() {}
function _temp2(s_0) {
  return s_0.setHideAiFeatures;
}
function _temp(s) {
  return s.hideAiFeatures;
}